/**
 * TUVI - Detalle Proveedor (Rediseñado)
 * Vista compacta y profesional con información agrupada
 */

import React, { useState, useEffect } from 'react';
import ModalDescuentoProveedor from './ModalDescuentoProveedor';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import * as XLSX from 'xlsx';
import './DetalleProveedor.css';
import ModalEditarProveedor from './ModalEditarProveedor';

const DetalleProveedor = ({ proveedorId, onVolver }) => {
  const { error: showError, success } = useNotification();
  const [proveedor, setProveedor] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [busquedaFacturas, setBusquedaFacturas] = useState('');
  const [paginaFacturas, setPaginaFacturas] = useState(1);
  const FACTURAS_POR_PAGINA = 15;
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados para modales
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
  const [tiposProveedor, setTiposProveedor] = useState({});
  // Descuentos pactados con este proveedor.
  const [descuentos, setDescuentos] = useState([]);
  const [resumenDesc, setResumenDesc] = useState({ total_registrado: 0, total_aplicado: 0, total_saldo: 0 });
  const [modalDescuento, setModalDescuento] = useState(null);

  useEffect(() => {
    cargarTiposProveedor();
    cargarDatos();
  }, [proveedorId]);

  const cargarTiposProveedor = async () => {
    try {
      const data = await client.query(`{ tiposProveedor { id codigo nombre color } }`);
      const tiposObj = {};
      (data.tiposProveedor || []).forEach(tipo => {
        tiposObj[tipo.codigo] = { nombre: tipo.nombre, color: tipo.color || '#607d8b' };
      });
      setTiposProveedor(tiposObj);
    } catch (error) {
      console.error('Error al cargar tipos de proveedor:', error);
      setTiposProveedor({
        materiales: { nombre: 'Materiales', color: '#ff9800' },
        mano_obra: { nombre: 'Mano de Obra', color: '#2196f3' },
        servicios: { nombre: 'Servicios', color: '#4caf50' },
        transporte: { nombre: 'Transporte', color: '#9c27b0' },
        equipos: { nombre: 'Equipos', color: '#f44336' },
        otro: { nombre: 'Otro', color: '#607d8b' }
      });
    }
  };

  useEffect(() => {
    if (proveedor) {
      cargarFacturas();
    }
  }, [proveedor]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const data = await client.query(`query($id: Int!) { proveedor(id: $id) {
        id nombre tipo clasificacion nit contacto_nombre telefono email direccion ciudad
        banco numero_cuenta tipo_cuenta titular_cuenta notas activo regimen_tributario tipo_contribuyente
        tipo_retencion_id porcentaje_retencion base_retencion base_retencion_texto
        retenciones { retencion_id tipo_retencion_id porcentaje_retencion base_retencion base_retencion_texto es_principal tipo_retencion_codigo tipo_retencion_nombre }
        tipo_retencion_codigo tipo_retencion_nombre total_facturas facturas_pendientes saldo_total_pendiente
      } }`, { id: proveedorId });

      if (data.proveedor) {
        setProveedor(data.proveedor);
      }
    } catch (err) {
      console.error('Error al cargar proveedor:', err);
      showError('Error al cargar información del proveedor');
    } finally {
      setLoading(false);
    }
  };

  const cargarFacturas = async () => {
    try {
      const data = await client.query(`query($pid: Int!) { facturas(proveedor_id: $pid) {
        facturas { id numero_factura fecha_emision fecha_vencimiento monto monto_pagado saldo_pendiente estado estado_calculado dias_vencido descripcion proveedor_nombre proyecto_nombre nombre_cliente apartamento retencion_fuente retencion_iva otras_retenciones }
        estadisticas { total_facturas monto_total monto_pagado_total saldo_pendiente_total facturas_pendientes facturas_pagadas facturas_parciales facturas_vencidas }
      } }`, { pid: proveedorId });
      setFacturas(data.facturas.facturas || []);
      setEstadisticas(data.facturas.estadisticas);
    } catch (error) {
      console.error('Error al cargar facturas:', error);
    }
  };

  const cargarDescuentos = async () => {
    try {
      const d = await client.query(`query($p: Int!) { descuentosProveedor(proveedor_id: $p) {
        resumen { total_registrado total_aplicado total_saldo cantidad }
        descuentos { id motivo valor valor_aplicado saldo estado fecha
          proyecto_nombre apartamento capitulo_nombre usuario_nombre
          aplicaciones { numero_factura valor_aplicado } }
      } }`, { p: proveedorId });
      setDescuentos(d.descuentosProveedor?.descuentos || []);
      setResumenDesc(d.descuentosProveedor?.resumen || { total_registrado: 0, total_aplicado: 0, total_saldo: 0 });
    } catch (e) {
      console.error('Error al cargar descuentos:', e);
    }
  };

  useEffect(() => {
    if (proveedorId) cargarDescuentos();
  }, [proveedorId]);

  const anularDescuento = async (d) => {
    if (!window.confirm(`¿Anular el descuento de ${formatMoney(d.valor)}?\n\nMotivo: ${d.motivo}\n\nEl gasto del capítulo "${d.capitulo_nombre}" volverá a subir.`)) return;
    try {
      const r = await client.mutate(
        `mutation($id: Int!) { anularDescuentoProveedor(id: $id) { success message } }`, { id: d.id });
      if (r.anularDescuentoProveedor.success) {
        success('Descuento anulado');
        cargarDescuentos();
      } else {
        showError(r.anularDescuentoProveedor.message || 'No se pudo anular');
      }
    } catch { showError('Error al anular el descuento'); }
  };

  const handleProveedorActualizado = () => {
    cargarDatos();
  };

  const defaultTipo = { nombre: 'Otro', color: '#607d8b' };

  const exportarExcelDetalle = async () => {
    try {
      const tipoInfo = tiposProveedor[proveedor.tipo] || tiposProveedor.otro || defaultTipo;

      // Cargar historial de pagos de todas las facturas
      const pagosHistorial = [];
      for (const factura of facturas) {
        try {
          const pData = await client.query(`query($fid: Int!) { pagosFactura(factura_id: $fid) { pagos { id monto_pago fecha_pago metodo_pago referencia banco notas } } }`, { fid: factura.id });
          const data = { success: true, pagos: pData.pagosFactura.pagos };
          if (data.success && data.pagos) {
            data.pagos.forEach(pago => {
              pagosHistorial.push({
                ...pago,
                numero_factura: factura.numero_factura,
                factura_monto: factura.monto
              });
            });
          }
        } catch (e) {
          console.error(`Error cargando pagos de factura ${factura.numero_factura}:`, e);
        }
      }

      // === HOJA 1: Información del Proveedor ===
      const infoProveedorData = [
        ['INFORMACIÓN DETALLADA DEL PROVEEDOR'],
        ['Fecha de Exportación:', new Date().toLocaleDateString('es-CO')],
        ['Hora:', new Date().toLocaleTimeString('es-CO')],
        [''],
        ['═══════════════════════════════════════════'],
        ['DATOS GENERALES'],
        ['═══════════════════════════════════════════'],
        ['Nombre:', proveedor.nombre || ''],
        ['Tipo:', tipoInfo.nombre || ''],
        ['NIT:', proveedor.nit || ''],
        ['Fecha Registro:', proveedor.fecha_creacion ? new Date(proveedor.fecha_creacion).toLocaleDateString('es-CO') : 'No disponible'],
        [''],
        ['═══════════════════════════════════════════'],
        ['CONFIGURACIÓN TRIBUTARIA'],
        ['═══════════════════════════════════════════'],
        ['Régimen Tributario:', getNombreRegimen(proveedor.regimen_tributario)],
        [''],
      ];

      // Agregar todas las retenciones configuradas
      if (proveedor.retenciones && proveedor.retenciones.length > 0) {
        infoProveedorData.push(['RETENCIONES CONFIGURADAS:']);
        proveedor.retenciones.forEach((ret, index) => {
          const esPrincipal = ret.es_principal == 1 ? ' (PRINCIPAL)' : '';
          infoProveedorData.push([
            `  ${index + 1}. ${ret.tipo_retencion_nombre}${esPrincipal}`,
            `${ret.porcentaje_retencion}% - Base: ${ret.base_retencion_texto || formatMoney(ret.base_retencion)}`
          ]);
        });
      } else {
        infoProveedorData.push(['Sin retenciones configuradas']);
      }

      infoProveedorData.push(
        [''],
        ['═══════════════════════════════════════════'],
        ['DATOS DE CONTACTO'],
        ['═══════════════════════════════════════════'],
        ['Nombre Contacto:', proveedor.contacto_nombre || 'No especificado'],
        ['Teléfono:', proveedor.telefono || 'No especificado'],
        ['Email:', proveedor.email || 'No especificado'],
        ['Ciudad:', proveedor.ciudad || 'No especificada'],
        ['Dirección:', proveedor.direccion || 'No especificada'],
        [''],
        ['═══════════════════════════════════════════'],
        ['INFORMACIÓN BANCARIA'],
        ['═══════════════════════════════════════════'],
        ['Banco:', proveedor.banco || 'No especificado'],
        ['Tipo de Cuenta:', proveedor.tipo_cuenta || 'No especificado'],
        ['Número de Cuenta:', proveedor.numero_cuenta || 'No especificado'],
        [''],
        ['═══════════════════════════════════════════'],
        ['RESUMEN FINANCIERO'],
        ['═══════════════════════════════════════════'],
        ['Total Facturas:', estadisticas?.total_facturas || 0],
        ['Monto Total Facturado:', formatMoney(estadisticas?.monto_total || 0)],
        ['Total Pagado:', formatMoney(estadisticas?.monto_pagado_total || 0)],
        ['Saldo Pendiente:', formatMoney(estadisticas?.saldo_pendiente_total || 0)],
        ['Facturas Vencidas:', estadisticas?.facturas_vencidas || 0],
        ['Total Pagos Realizados:', pagosHistorial.length],
      );

      if (proveedor.notas) {
        infoProveedorData.push(
          [''],
          ['═══════════════════════════════════════════'],
          ['NOTAS / OBSERVACIONES'],
          ['═══════════════════════════════════════════'],
          [proveedor.notas]
        );
      }

      // === HOJA 2: Facturas Detalladas ===
      const facturasData = [
        ['FACTURAS DEL PROVEEDOR: ' + proveedor.nombre],
        ['Exportado el: ' + new Date().toLocaleDateString('es-CO')],
        [''],
        [
          'Número Factura', 'Estado', 'Subtotal', 'IVA', 'Monto Total',
          'Ret. Fuente', 'Ret. IVA', 'Otras Ret.', 'Total Retenciones', 'Valor Neto',
          'Monto Pagado', 'Saldo Pendiente', '% Pagado', 'Fecha Emisión', 'Fecha Vencimiento',
          'Días Vencido', 'Proyecto', 'Descripción'
        ]
      ];

      let totalSubtotal = 0;
      let totalIva = 0;
      let totalMonto = 0;
      let totalRetFuente = 0;
      let totalRetIva = 0;
      let totalOtrasRet = 0;
      let totalRetenciones = 0;
      let totalNeto = 0;
      let totalPagado = 0;
      let totalSaldo = 0;

      facturas.forEach(factura => {
        const esVencida = factura.dias_vencido > 0 && factura.estado !== 'pagado';
        const estadoFinal = esVencida ? 'vencido' : factura.estado;

        let estadoTexto = '';
        switch(estadoFinal) {
          case 'pendiente': estadoTexto = 'Pendiente'; break;
          case 'pagado_parcial': estadoTexto = 'Pago Parcial'; break;
          case 'pagado': estadoTexto = 'Pagado'; break;
          case 'vencido': estadoTexto = 'VENCIDA'; break;
          case 'cancelado': estadoTexto = 'Cancelada'; break;
          default: estadoTexto = estadoFinal;
        }

        const retFuente = parseFloat(factura.retencion_fuente) || 0;
        const retIva = parseFloat(factura.retencion_iva) || 0;
        const otrasRet = parseFloat(factura.otras_retenciones) || 0;
        const retTotal = retFuente + retIva + otrasRet;
        const monto = parseFloat(factura.monto) || 0;
        const valorNeto = monto - retTotal;
        const montoPagado = parseFloat(factura.monto_pagado) || 0;
        const saldoPendiente = parseFloat(factura.saldo_pendiente) || 0;
        const porcentajePagado = monto > 0 ? ((montoPagado / monto) * 100).toFixed(1) : 0;

        // Acumular totales
        totalSubtotal += parseFloat(factura.subtotal) || 0;
        totalIva += parseFloat(factura.iva) || 0;
        totalMonto += monto;
        totalRetFuente += retFuente;
        totalRetIva += retIva;
        totalOtrasRet += otrasRet;
        totalRetenciones += retTotal;
        totalNeto += valorNeto;
        totalPagado += montoPagado;
        totalSaldo += saldoPendiente;

        facturasData.push([
          factura.numero_factura || '',
          estadoTexto,
          factura.subtotal || 0,
          factura.iva || 0,
          monto,
          retFuente,
          retIva,
          otrasRet,
          retTotal,
          valorNeto,
          montoPagado,
          saldoPendiente,
          `${porcentajePagado}%`,
          factura.fecha_emision ? new Date(factura.fecha_emision).toLocaleDateString('es-CO') : '',
          factura.fecha_vencimiento ? new Date(factura.fecha_vencimiento).toLocaleDateString('es-CO') : '',
          esVencida ? factura.dias_vencido : 0,
          factura.proyecto_nombre || 'Sin proyecto',
          factura.descripcion || ''
        ]);
      });

      // Agregar fila de totales
      facturasData.push([]);
      facturasData.push([
        'TOTALES', '', totalSubtotal, totalIva, totalMonto,
        totalRetFuente, totalRetIva, totalOtrasRet, totalRetenciones, totalNeto,
        totalPagado, totalSaldo, '', '', '', '', '', ''
      ]);

      // === HOJA 3: Historial de Pagos ===
      const pagosData = [
        ['HISTORIAL DE PAGOS - ' + proveedor.nombre],
        ['Exportado el: ' + new Date().toLocaleDateString('es-CO')],
        [''],
        ['Factura', 'Monto Factura', 'Fecha Pago', 'Monto Pagado', 'Método Pago', 'Referencia', 'Observaciones']
      ];

      let totalPagosRealizados = 0;
      pagosHistorial.forEach(pago => {
        totalPagosRealizados += parseFloat(pago.monto) || 0;
        pagosData.push([
          pago.numero_factura || '',
          pago.factura_monto || 0,
          pago.fecha_pago ? new Date(pago.fecha_pago).toLocaleDateString('es-CO') : '',
          pago.monto || 0,
          pago.metodo_pago || '',
          pago.referencia || '',
          pago.observaciones || ''
        ]);
      });

      if (pagosHistorial.length === 0) {
        pagosData.push(['No hay pagos registrados']);
      } else {
        pagosData.push([]);
        pagosData.push(['', '', 'TOTAL PAGADO:', totalPagosRealizados, '', '', '']);
      }

      // === HOJA 4: Resumen por Estado ===
      const estadosMap = {
        'pendiente': { count: 0, monto: 0, saldo: 0, retenciones: 0 },
        'pagado_parcial': { count: 0, monto: 0, saldo: 0, retenciones: 0 },
        'pagado': { count: 0, monto: 0, saldo: 0, retenciones: 0 },
        'vencido': { count: 0, monto: 0, saldo: 0, retenciones: 0 },
        'cancelado': { count: 0, monto: 0, saldo: 0, retenciones: 0 }
      };

      facturas.forEach(factura => {
        const esVencida = factura.dias_vencido > 0 && factura.estado !== 'pagado';
        const estadoFinal = esVencida ? 'vencido' : factura.estado;
        const retTotal = (parseFloat(factura.retencion_fuente) || 0) +
                         (parseFloat(factura.retencion_iva) || 0) +
                         (parseFloat(factura.otras_retenciones) || 0);

        if (estadosMap[estadoFinal]) {
          estadosMap[estadoFinal].count++;
          estadosMap[estadoFinal].monto += parseFloat(factura.monto || 0);
          estadosMap[estadoFinal].saldo += parseFloat(factura.saldo_pendiente || 0);
          estadosMap[estadoFinal].retenciones += retTotal;
        }
      });

      const resumenData = [
        ['RESUMEN POR ESTADO DE FACTURAS'],
        ['Proveedor: ' + proveedor.nombre],
        [''],
        ['Estado', 'Cantidad', 'Monto Total', 'Total Retenciones', 'Saldo Pendiente'],
        ['Pendientes', estadosMap.pendiente.count, estadosMap.pendiente.monto, estadosMap.pendiente.retenciones, estadosMap.pendiente.saldo],
        ['Pagos Parciales', estadosMap.pagado_parcial.count, estadosMap.pagado_parcial.monto, estadosMap.pagado_parcial.retenciones, estadosMap.pagado_parcial.saldo],
        ['Pagadas', estadosMap.pagado.count, estadosMap.pagado.monto, estadosMap.pagado.retenciones, estadosMap.pagado.saldo],
        ['Vencidas', estadosMap.vencido.count, estadosMap.vencido.monto, estadosMap.vencido.retenciones, estadosMap.vencido.saldo],
        ['Canceladas', estadosMap.cancelado.count, estadosMap.cancelado.monto, estadosMap.cancelado.retenciones, estadosMap.cancelado.saldo],
        [''],
        ['TOTALES',
          Object.values(estadosMap).reduce((sum, e) => sum + e.count, 0),
          Object.values(estadosMap).reduce((sum, e) => sum + e.monto, 0),
          Object.values(estadosMap).reduce((sum, e) => sum + e.retenciones, 0),
          Object.values(estadosMap).reduce((sum, e) => sum + e.saldo, 0)
        ]
      ];

      // === HOJA 5: Facturas Pendientes (urgentes primero) ===
      const pendientesData = [
        ['FACTURAS PENDIENTES DE PAGO - ' + proveedor.nombre],
        ['Ordenadas por urgencia (días vencido)'],
        [''],
        ['Número Factura', 'Estado', 'Monto Total', 'Valor Neto', 'Saldo Pendiente', 'Fecha Vencimiento', 'Días Vencido', 'Proyecto']
      ];

      const facturasPendientes = facturas
        .filter(f => f.estado !== 'pagado' && f.estado !== 'cancelado')
        .sort((a, b) => (b.dias_vencido || 0) - (a.dias_vencido || 0));

      facturasPendientes.forEach(factura => {
        const esVencida = factura.dias_vencido > 0;
        const retTotal = (parseFloat(factura.retencion_fuente) || 0) +
                         (parseFloat(factura.retencion_iva) || 0) +
                         (parseFloat(factura.otras_retenciones) || 0);
        const valorNeto = (parseFloat(factura.monto) || 0) - retTotal;

        pendientesData.push([
          factura.numero_factura || '',
          esVencida ? 'VENCIDA' : (factura.estado === 'pagado_parcial' ? 'Pago Parcial' : 'Pendiente'),
          factura.monto || 0,
          valorNeto,
          factura.saldo_pendiente || 0,
          factura.fecha_vencimiento ? new Date(factura.fecha_vencimiento).toLocaleDateString('es-CO') : '',
          esVencida ? factura.dias_vencido : 0,
          factura.proyecto_nombre || 'Sin proyecto'
        ]);
      });

      if (facturasPendientes.length === 0) {
        pendientesData.push(['No hay facturas pendientes de pago']);
      }

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();

      const wsInfo = XLSX.utils.aoa_to_sheet(infoProveedorData);
      wsInfo['!cols'] = [{ wch: 30 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(wb, wsInfo, 'Info Proveedor');

      const wsFacturas = XLSX.utils.aoa_to_sheet(facturasData);
      wsFacturas['!cols'] = [
        { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
        { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 14 },
        { wch: 12 }, { wch: 25 }, { wch: 35 }
      ];
      XLSX.utils.book_append_sheet(wb, wsFacturas, 'Facturas');

      const wsPagos = XLSX.utils.aoa_to_sheet(pagosData);
      wsPagos['!cols'] = [
        { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 15 }, { wch: 20 }, { wch: 35 }
      ];
      XLSX.utils.book_append_sheet(wb, wsPagos, 'Historial Pagos');

      const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
      wsResumen['!cols'] = [{ wch: 18 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen Estados');

      const wsPendientes = XLSX.utils.aoa_to_sheet(pendientesData);
      wsPendientes['!cols'] = [
        { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
        { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, wsPendientes, 'Pendientes');

      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Proveedor_${proveedor.nombre.replace(/[^a-zA-Z0-9]/g, '_')}_${fecha}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);

    } catch (err) {
      console.error('Error al exportar Excel:', err);
      showError('Error al generar el archivo Excel');
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    if (!fecha) return 'No definida';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getNombreRegimen = (regimen) => {
    switch(regimen) {
      case 'simple': return 'Régimen Simple';
      case 'gran_contribuyente': return 'Gran Contribuyente';
      default: return 'Régimen Común';
    }
  };

  if (loading || !proveedor) {
    return (
      <div className="detalle-loading">
        <div className="construction-loader">
          <div className="loader-bricks">
            <div className="loader-brick"></div>
            <div className="loader-brick"></div>
            <div className="loader-brick"></div>
          </div>
        </div>
        <p>Cargando información del proveedor...</p>
      </div>
    );
  }

  const tipoInfo = tiposProveedor[proveedor.tipo] || tiposProveedor.otro || defaultTipo;
  const tieneRetencion = proveedor.retenciones?.length > 0;

  return (
    <div className="detalle-proveedor-v2">
      {/* Header compacto */}
      <div className="header-compacto">
        <button className="btn-volver" onClick={onVolver}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Volver
        </button>

        <div className="header-centro">
          <div className="proveedor-avatar-sm" style={{ background: tipoInfo.color }}>
            {proveedor.nombre.charAt(0).toUpperCase()}
          </div>
          <div className="header-info">
            <h1>{proveedor.nombre}</h1>
            <div className="header-meta">
              <span className="tipo-badge-sm" style={{ background: tipoInfo.color }}>
                {tipoInfo.nombre}
              </span>
              {proveedor.nit && <span className="nit-badge">NIT: {proveedor.nit}</span>}
              {tieneRetencion && (
                <span className="retencion-badge-sm">
                  {proveedor.porcentaje_retencion}% Ret.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="header-acciones">
          <button className="btn-icon" onClick={exportarExcelDetalle} title="Exportar Excel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </button>
          <button className="btn-icon" onClick={() => setMostrarModalEditar(true)} title="Editar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Panel principal con 2 columnas */}
      <div className="panel-principal">
        {/* Columna izquierda: Info del proveedor */}
        <div className="columna-info">
          {/* Resumen financiero compacto */}
          {estadisticas && (
            <div className="resumen-financiero">
              <div className="stat-mini total">
                <span className="stat-valor">{estadisticas.total_facturas}</span>
                <span className="stat-etiqueta">Facturas</span>
              </div>
              <div className="stat-mini monto">
                <span className="stat-valor">{formatMoney(estadisticas.monto_total)}</span>
                <span className="stat-etiqueta">Facturado</span>
              </div>
              <div className="stat-mini pagado">
                <span className="stat-valor">{formatMoney(estadisticas.monto_pagado_total)}</span>
                <span className="stat-etiqueta">Pagado</span>
              </div>
              <div className="stat-mini pendiente">
                <span className="stat-valor">{formatMoney(estadisticas.saldo_pendiente_total)}</span>
                <span className="stat-etiqueta">Pendiente</span>
              </div>
              {estadisticas.facturas_vencidas > 0 && (
                <div className="stat-mini vencido">
                  <span className="stat-valor">{estadisticas.facturas_vencidas}</span>
                  <span className="stat-etiqueta">Vencidas</span>
                </div>
              )}
            </div>
          )}

          {/* Retenciones (si aplica) */}
          {tieneRetencion && (
            <div className="seccion-compacta retenciones">
              <h3>Retenciones Configuradas</h3>
              <div className="retenciones-lista">
                {proveedor.retenciones.map((ret, index) => (
                  <div key={index} className={`retencion-item ${ret.es_principal == 1 ? 'principal' : ''}`}>
                    <div className="retencion-info">
                      <span className="retencion-tipo">{ret.tipo_retencion_nombre}</span>
                      {ret.es_principal == 1 && <span className="badge-ppal">Principal</span>}
                    </div>
                    <div className="retencion-valores">
                      <span className="retencion-pct">{ret.porcentaje_retencion}%</span>
                      {ret.base_retencion > 0 && (
                        <span className="retencion-base">Base: {ret.base_retencion_texto || formatMoney(ret.base_retencion)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="regimen-info">
                <span className="regimen-label">Régimen:</span>
                <span className="regimen-valor">{getNombreRegimen(proveedor.regimen_tributario)}</span>
              </div>
            </div>
          )}

          {/* Datos de contacto compactos */}
          <div className="seccion-compacta contacto">
            <h3>Contacto</h3>
            <div className="datos-grid">
              {proveedor.contacto_nombre && (
                <div className="dato-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>{proveedor.contacto_nombre}</span>
                </div>
              )}
              {proveedor.telefono && (
                <div className="dato-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                  <span>{proveedor.telefono}</span>
                </div>
              )}
              {proveedor.email && (
                <div className="dato-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <span>{proveedor.email}</span>
                </div>
              )}
              {proveedor.ciudad && (
                <div className="dato-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{proveedor.ciudad}</span>
                </div>
              )}
              {proveedor.direccion && (
                <div className="dato-item full">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <span>{proveedor.direccion}</span>
                </div>
              )}
            </div>
          </div>

          {/* Info bancaria */}
          {(proveedor.banco || proveedor.numero_cuenta) && (
            <div className="seccion-compacta bancaria">
              <h3>Datos Bancarios</h3>
              <div className="datos-grid">
                {proveedor.banco && (
                  <div className="dato-item">
                    <span className="dato-label">Banco:</span>
                    <span>{proveedor.banco}</span>
                  </div>
                )}
                {proveedor.tipo_cuenta && (
                  <div className="dato-item">
                    <span className="dato-label">Tipo:</span>
                    <span>{proveedor.tipo_cuenta}</span>
                  </div>
                )}
                {proveedor.numero_cuenta && (
                  <div className="dato-item full">
                    <span className="dato-label">Cuenta:</span>
                    <span className="cuenta-numero">{proveedor.numero_cuenta}</span>
                  </div>
                )}
                {proveedor.titular_cuenta && (
                  <div className="dato-item full">
                    <span className="dato-label">A nombre de:</span>
                    <span>{proveedor.titular_cuenta}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notas */}
          {proveedor.notas && (
            <div className="seccion-compacta notas">
              <h3>Notas</h3>
              <p>{proveedor.notas}</p>
            </div>
          )}

        </div>

        {/* Columna derecha: Resumen de facturas (solo lectura) */}
        <div className="columna-facturas">
          {/* Descuentos pactados con este proveedor */}
          <div className="dpv-seccion">
            <div className="dpv-head">
              <h3>Descuentos</h3>
              <button className="dp-btn dp-btn-ok" onClick={() => setModalDescuento(proveedor)}>
                + Registrar descuento
              </button>
            </div>

            {descuentos.length === 0 ? (
              <div className="dpv-vacio">Este proveedor no tiene descuentos registrados.</div>
            ) : (
              <>
                <div className="dpv-resumen">
                  <div className="dpv-r">REGISTRADO<strong>{formatMoney(resumenDesc.total_registrado)}</strong></div>
                  <div className="dpv-r">APLICADO<strong>{formatMoney(resumenDesc.total_aplicado)}</strong></div>
                  <div className="dpv-r saldo">SALDO DISPONIBLE<strong>{formatMoney(resumenDesc.total_saldo)}</strong></div>
                </div>

                <div className="dpv-tabla-wrap">
                  <table className="dpv-tabla">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Motivo</th>
                        <th>Imputado a</th>
                        <th className="dpv-num">Valor</th>
                        <th className="dpv-num">Saldo</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {descuentos.map(d => (
                        <tr key={d.id}>
                          <td className="dpv-fecha">{d.fecha}</td>
                          <td className="dpv-motivo">
                            {d.motivo}
                            {d.aplicaciones.length > 0 && (
                              <div className="dpv-aplic">
                                Usado en: {d.aplicaciones.map(a => `#${a.numero_factura} (${formatMoney(a.valor_aplicado)})`).join(' · ')}
                              </div>
                            )}
                          </td>
                          <td className="dpv-ubic">
                            {d.proyecto_nombre}{d.apartamento ? ` · ${d.apartamento}` : ''}
                            <div>{d.capitulo_nombre}</div>
                          </td>
                          <td className="dpv-num">{formatMoney(d.valor)}</td>
                          <td className="dpv-num">{formatMoney(d.saldo)}</td>
                          <td><span className={`dpv-badge ${d.estado}`}>{d.estado}</span></td>
                          <td>
                            {d.estado === 'pendiente' && (
                              <button className="dpv-anular" onClick={() => anularDescuento(d)}>Anular</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="filtros-inline">
            <span className="filtros-titulo">Resumen de Facturas</span>
          </div>

          {facturas.length === 0 ? (
            <div className="empty-facturas">
              <p>No hay facturas registradas para este proveedor</p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                Las facturas se gestionan desde el módulo centralizado de Facturas
              </p>
            </div>
          ) : (() => {
            const q = (busquedaFacturas || '').toLowerCase().trim();
            const facturasFiltradas = q
              ? facturas.filter(f =>
                  (f.numero_factura || '').toLowerCase().includes(q)
                  || (f.proyecto_nombre || '').toLowerCase().includes(q)
                  || (f.descripcion || '').toLowerCase().includes(q)
                  || (f.nombre_cliente || '').toLowerCase().includes(q)
                  || (f.apartamento || '').toLowerCase().includes(q)
                )
              : facturas;
            const totalPaginas = Math.max(1, Math.ceil(facturasFiltradas.length / FACTURAS_POR_PAGINA));
            const pagActual = Math.min(paginaFacturas, totalPaginas);
            const desde = (pagActual - 1) * FACTURAS_POR_PAGINA;
            const facturasPagina = facturasFiltradas.slice(desde, desde + FACTURAS_POR_PAGINA);
            return (
            <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Buscar por # factura, proyecto, apto, descripción o cliente..."
                value={busquedaFacturas}
                onChange={e => { setBusquedaFacturas(e.target.value); setPaginaFacturas(1); }}
                style={{ flex: 1, minWidth: 220, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
              />
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {facturasFiltradas.length} resultado{facturasFiltradas.length !== 1 ? 's' : ''}
                {facturasFiltradas.length !== facturas.length && ` de ${facturas.length}`}
              </span>
            </div>
            <div className="lista-facturas-compacta">
              {facturasPagina.map((factura) => {
                const esVencida = factura.dias_vencido > 0 && factura.estado !== 'pagado';
                const estadoFinal = esVencida ? 'vencido' : factura.estado;

                return (
                  <div key={factura.id} className={`factura-row ${estadoFinal}`}>
                    <div className="factura-row-header">
                      <div className="factura-id">
                        <strong>#{factura.numero_factura}</strong>
                        <span className={`estado-chip ${estadoFinal}`}>
                          {estadoFinal === 'pendiente' && 'Pendiente'}
                          {estadoFinal === 'pagado_parcial' && 'Parcial'}
                          {estadoFinal === 'pagado' && 'Pagado'}
                          {estadoFinal === 'vencido' && 'Vencida'}
                          {estadoFinal === 'cancelado' && 'Cancelada'}
                        </span>
                      </div>
                      <div className="factura-header-spacer"></div>
                      <div className="factura-monto">
                        <span className="monto-total">{formatMoney(factura.monto)}</span>
                      </div>
                    </div>
                    <div className="factura-row-body">
                      <div className="factura-fechas">
                        <span>Emisión: {formatDate(factura.fecha_emision)}</span>
                      </div>
                      <div className="factura-proyecto">
                        {factura.proyecto_nombre || 'Sin proyecto'}
                        {factura.apartamento && (
                          <span className="factura-apto"> · Apto {factura.apartamento}</span>
                        )}
                      </div>
                      {factura.monto_pagado > 0 && factura.estado !== 'pagado' && (
                        <div className="factura-pagos">
                          <span className="pagado">Pagado: {formatMoney(factura.monto_pagado)}</span>
                          <span className="saldo">Saldo: {formatMoney(factura.saldo_pendiente)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {totalPaginas > 1 && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setPaginaFacturas(p => Math.max(1, p - 1))}
                  disabled={pagActual === 1}
                  style={{ padding: '6px 14px', border: '1px solid #d1d5db', background: pagActual === 1 ? '#f3f4f6' : '#fff', borderRadius: 6, cursor: pagActual === 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}
                >← Anterior</button>
                <span style={{ fontSize: 13, color: '#374151' }}>Página {pagActual} de {totalPaginas}</span>
                <button
                  type="button"
                  onClick={() => setPaginaFacturas(p => Math.min(totalPaginas, p + 1))}
                  disabled={pagActual === totalPaginas}
                  style={{ padding: '6px 14px', border: '1px solid #d1d5db', background: pagActual === totalPaginas ? '#f3f4f6' : '#fff', borderRadius: 6, cursor: pagActual === totalPaginas ? 'not-allowed' : 'pointer', fontSize: 13 }}
                >Siguiente →</button>
              </div>
            )}
            </>
            );
          })()}
        </div>
      </div>

      {/* Modales */}
      <ModalEditarProveedor
        show={mostrarModalEditar}
        onClose={() => setMostrarModalEditar(false)}
        proveedor={proveedor}
        onProveedorActualizado={handleProveedorActualizado}
      />

      {modalDescuento && (
        <ModalDescuentoProveedor
          proveedor={modalDescuento}
          onCerrar={() => setModalDescuento(null)}
          onGuardado={cargarDescuentos}
        />
      )}
    </div>
  );
};

export default DetalleProveedor;
